/**
 * Floating welcome video player.
 * Attaches video sources only after the page has loaded so it never contends
 * with the rest of the storefront. The player stays hidden until playback can start.
 */
(function (global) {
  var MOBILE_BREAKPOINT = 768

  function isMobileViewport(width, breakpoint) {
    var viewportWidth = typeof width === 'number' ? width : getViewportWidth()
    var bp = typeof breakpoint === 'number' ? breakpoint : MOBILE_BREAKPOINT
    return viewportWidth < bp
  }

  function getViewportWidth() {
    if (typeof window === 'undefined') return 1024
    return window.innerWidth || (document.documentElement && document.documentElement.clientWidth) || 1024
  }

  function isIosLike(userAgent, platform, maxTouchPoints) {
    var ua = userAgent == null && typeof navigator !== 'undefined' ? navigator.userAgent : userAgent || ''
    var plat = platform == null && typeof navigator !== 'undefined' ? navigator.platform : platform || ''
    var points =
      typeof maxTouchPoints === 'number'
        ? maxTouchPoints
        : typeof navigator !== 'undefined'
          ? navigator.maxTouchPoints || 0
          : 0
    return /iPhone|iPad|iPod/i.test(ua) || (plat === 'MacIntel' && points > 1)
  }

  function isAndroidMobile(userAgent) {
    var ua = userAgent == null && typeof navigator !== 'undefined' ? navigator.userAgent : userAgent || ''
    return /Android/i.test(ua)
  }

  function shouldUseNativeFullscreen(options) {
    var opts = options || {}
    if (opts.forceMobile) return true
    if (opts.forceDesktop) return false
    return (
      isMobileViewport(opts.width, opts.breakpoint) ||
      isIosLike(opts.userAgent, opts.platform, opts.maxTouchPoints) ||
      isAndroidMobile(opts.userAgent)
    )
  }

  function scheduleDeferredLoad(callback, options) {
    var opts = options || {}
    var readyState = opts.readyState
    if (readyState == null && typeof document !== 'undefined') {
      readyState = document.readyState
    }

    if (opts.designMode) {
      callback()
      return
    }

    var idle =
      typeof opts.idle === 'function'
        ? opts.idle
        : typeof requestIdleCallback === 'function'
          ? requestIdleCallback
          : null
    var timeout = typeof opts.timeout === 'function' ? opts.timeout : setTimeout

    var run = function () {
      if (idle) {
        idle(
          function () {
            callback()
          },
          { timeout: opts.idleTimeout || 4000 }
        )
      } else {
        timeout(callback, opts.delay || 400)
      }
    }

    if (readyState === 'complete') {
      run()
    } else if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener('load', run, { once: true })
    } else {
      callback()
    }
  }

  function getScrollShrinkThreshold(viewportHeight) {
    var height = typeof viewportHeight === 'number' ? viewportHeight : 800
    return Math.max(280, Math.round(height * 0.4))
  }

  function shouldShrinkOnScroll(scrollY, threshold) {
    return Number(scrollY || 0) >= Number(threshold || 0)
  }

  function parseDataSources(value) {
    if (!value) return []
    try {
      var parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch (error) {
      return []
    }
  }

  function sortVideoSources(sources) {
    return sources.slice().sort(function (a, b) {
      var aMp4 = a && a.type && String(a.type).indexOf('mp4') !== -1 ? 0 : 1
      var bMp4 = b && b.type && String(b.type).indexOf('mp4') !== -1 ? 0 : 1
      return aMp4 - bMp4
    })
  }

  function restartFromBeginning(video) {
    if (!video) return false
    try {
      video.currentTime = 0
      return true
    } catch (error) {
      return false
    }
  }

  function attachDeferredSources(video, sources) {
    if (!video) return 0
    var attached = 0

    if (sources && sources.length) {
      var sorted = sortVideoSources(sources)
      for (var i = 0; i < sorted.length; i++) {
        var item = sorted[i]
        if (!item || !item.src) continue
        if (typeof document === 'undefined' || typeof document.createElement !== 'function') continue
        var sourceEl = document.createElement('source')
        sourceEl.setAttribute('src', item.src)
        if (item.type) sourceEl.setAttribute('type', item.type)
        video.appendChild(sourceEl)
        attached += 1
      }
    } else {
      var existing = video.querySelectorAll('source[data-src]')
      for (var j = 0; j < existing.length; j++) {
        var source = existing[j]
        var src = source.getAttribute('data-src')
        if (src && !source.getAttribute('src')) {
          source.setAttribute('src', src)
          attached += 1
        }
      }
    }

    if (attached > 0 && typeof video.load === 'function') {
      video.load()
    }
    return attached
  }

  var api = {
    MOBILE_BREAKPOINT: MOBILE_BREAKPOINT,
    isMobileViewport: isMobileViewport,
    isIosLike: isIosLike,
    isAndroidMobile: isAndroidMobile,
    shouldUseNativeFullscreen: shouldUseNativeFullscreen,
    scheduleDeferredLoad: scheduleDeferredLoad,
    parseDataSources: parseDataSources,
    sortVideoSources: sortVideoSources,
    restartFromBeginning: restartFromBeginning,
    getScrollShrinkThreshold: getScrollShrinkThreshold,
    shouldShrinkOnScroll: shouldShrinkOnScroll,
    attachDeferredSources: attachDeferredSources,
  }

  if (typeof HTMLElement !== 'undefined') {
    class WelcomeVideoPlayer extends HTMLElement {
      connectedCallback() {
        if (this._bound) return
        this._bound = true
        this.loaded = false
        this.video = this.querySelector('[data-welcome-video]')
        this.playButton = this.querySelector('[data-welcome-video-play]')
        this.closeButton = this.querySelector('[data-welcome-video-close]')
        this.dismissButton = this.querySelector('[data-welcome-video-dismiss]')
        this.backdrop = this.querySelector('[data-welcome-video-backdrop]')

        this._onPlayClick = this.onPlayClick.bind(this)
        this._onCloseClick = this.collapse.bind(this)
        this._onDismissClick = this.onDismissClick.bind(this)
        this._onKeyDown = this.onKeyDown.bind(this)
        this._onFullscreenChange = this.onFullscreenChange.bind(this)
        this._onPlaying = this.onPlaying.bind(this)
        this._onCanPlay = this.onCanPlay.bind(this)
        this._onNativeEnter = this.onNativeFullscreenEnter.bind(this)
        this._onNativeExit = this.onNativeFullscreenExit.bind(this)
        this._onScroll = this.onScroll.bind(this)

        if (this.playButton) {
          this.playButton.addEventListener('click', this._onPlayClick)
        }
        if (this.closeButton) {
          this.closeButton.addEventListener('click', this._onCloseClick)
        }
        if (this.dismissButton) {
          this.dismissButton.addEventListener('click', this._onDismissClick)
        }
        if (this.backdrop) {
          this.backdrop.addEventListener('click', this._onCloseClick)
        }
        document.addEventListener('keydown', this._onKeyDown)
        document.addEventListener('fullscreenchange', this._onFullscreenChange)
        document.addEventListener('webkitfullscreenchange', this._onFullscreenChange)
        if (this.video) {
          this.video.addEventListener('playing', this._onPlaying)
          this.video.addEventListener('canplay', this._onCanPlay)
          this.video.addEventListener('webkitbeginfullscreen', this._onNativeEnter)
          this.video.addEventListener('webkitendfullscreen', this._onNativeExit)
        }

        window.addEventListener('scroll', this._onScroll, { passive: true })
        this.onScroll()

        var designMode = Boolean(global.Shopify && global.Shopify.designMode)
        var self = this
        scheduleDeferredLoad(
          function () {
            self.loadAndPlayPreview()
          },
          { designMode: designMode, idleTimeout: 4000, delay: 400 }
        )
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this._onKeyDown)
        document.removeEventListener('fullscreenchange', this._onFullscreenChange)
        document.removeEventListener('webkitfullscreenchange', this._onFullscreenChange)
        window.removeEventListener('scroll', this._onScroll)
      }

      getBreakpoint() {
        var attr = this.getAttribute('data-mobile-breakpoint')
        var parsed = attr ? parseInt(attr, 10) : MOBILE_BREAKPOINT
        return isNaN(parsed) ? MOBILE_BREAKPOINT : parsed
      }

      isMobile() {
        return shouldUseNativeFullscreen({
          forceMobile: this.hasAttribute('data-force-mobile'),
          forceDesktop: this.hasAttribute('data-force-desktop'),
          width: getViewportWidth(),
          breakpoint: this.getBreakpoint(),
        })
      }

      hidePlayer() {
        this.setAttribute('hidden', '')
        this.classList.add('is-dismissed')
        window.removeEventListener('scroll', this._onScroll)
        if (this.video) {
          this.video.pause()
        }
      }

      onScroll() {
        if (this.isExpanded() || this.isNativeFullscreen() || this.classList.contains('is-dismissed')) return
        var threshold = getScrollShrinkThreshold(window.innerHeight)
        if (shouldShrinkOnScroll(window.scrollY || window.pageYOffset || 0, threshold)) {
          this.classList.add('is-scrolled')
        } else {
          this.classList.remove('is-scrolled')
        }
      }

      loadAndPlayPreview() {
        if (!this.video || this.loaded) return
        var sources = parseDataSources(this.getAttribute('data-sources'))
        attachDeferredSources(this.video, sources.length ? sources : null)
        this.loaded = true
        this.playPreview()
      }

      playPreview() {
        if (!this.video) return
        this.video.muted = true
        this.video.defaultMuted = true
        this.video.playsInline = true
        this.video.setAttribute('playsinline', '')
        this.video.setAttribute('webkit-playsinline', '')
        this.video.loop = true
        this.video.controls = false
        var playPromise = this.video.play()
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {
            // Autoplay can be blocked; the player still appears once canplay fires.
          })
        }
      }

      onCanPlay() {
        this.classList.add('is-ready')
      }

      onPlaying() {
        this.classList.add('is-ready')
        this.classList.add('is-playing')
      }

      onPlayClick(event) {
        if (event) {
          event.preventDefault()
          event.stopPropagation()
        }
        if (this.isExpanded() || this.isNativeFullscreen()) return
        restartFromBeginning(this.video)
        if (this.isMobile()) {
          this.openMobileFullscreen()
        } else {
          this.expand()
        }
      }

      onDismissClick(event) {
        if (event) {
          event.preventDefault()
          event.stopPropagation()
        }
        if (this.isNativeFullscreen()) {
          this.exitNativeFullscreen()
        }
        this.collapse()
        this.hidePlayer()
      }

      enableSound() {
        if (!this.video) return
        this.video.muted = false
        this.video.defaultMuted = false
        this.video.removeAttribute('muted')
        var playPromise = this.video.play()
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {})
        }
      }

      mutePreview() {
        if (!this.video) return
        this.video.muted = true
        this.video.defaultMuted = true
        this.video.setAttribute('muted', '')
        this.video.controls = false
        this.video.playsInline = true
        this.video.setAttribute('playsinline', '')
        this.video.setAttribute('webkit-playsinline', '')
      }

      expand() {
        this.setAttribute('expanded', '')
        this.classList.add('is-expanded')
        this.classList.add('is-ready')
        if (this.playButton) this.playButton.setAttribute('hidden', '')
        if (this.dismissButton) this.dismissButton.setAttribute('hidden', '')
        if (this.backdrop) this.backdrop.removeAttribute('hidden')
        if (this.closeButton) {
          this.closeButton.removeAttribute('hidden')
          this.closeButton.focus({ preventScroll: true })
        }
        document.documentElement.classList.add('welcome-video-lock')
        this.enableSound()
        if (this.video) this.video.controls = true
      }

      collapse() {
        if (this.isNativeFullscreen()) {
          this.exitNativeFullscreen()
        }
        this.removeAttribute('expanded')
        this.classList.remove('is-expanded')
        if (this.playButton) this.playButton.removeAttribute('hidden')
        if (this.dismissButton) this.dismissButton.removeAttribute('hidden')
        if (this.backdrop) this.backdrop.setAttribute('hidden', '')
        if (this.closeButton) this.closeButton.setAttribute('hidden', '')
        var scrollX = window.scrollX
        var scrollY = window.scrollY
        document.documentElement.classList.remove('welcome-video-lock')
        window.scrollTo(scrollX, scrollY)
        this.mutePreview()
        if (!this.classList.contains('is-dismissed')) {
          this.playPreview()
        }
        if (this.playButton && typeof this.playButton.focus === 'function') {
          this.playButton.focus({ preventScroll: true })
        }
      }

      isExpanded() {
        return this.hasAttribute('expanded')
      }

      openMobileFullscreen() {
        if (!this.video) return
        var video = this.video
        var self = this

        video.muted = false
        video.defaultMuted = false
        video.removeAttribute('muted')
        video.controls = true

        var entered = false
        var markEntered = function () {
          entered = true
          self.onNativeFullscreenEnter()
        }

        if (typeof video.webkitEnterFullscreen === 'function') {
          try {
            video.webkitEnterFullscreen()
            if (video.webkitDisplayingFullscreen) markEntered()
          } catch (error) {
            // Fall through to the standard Fullscreen API.
          }
        }

        var playPromise = video.play()
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise
            .then(function () {
              if (entered || self.isNativeFullscreen()) return
              if (typeof video.webkitEnterFullscreen === 'function') {
                try {
                  video.webkitEnterFullscreen()
                } catch (error) {}
              }
            })
            .catch(function () {})
        }

        var request = video.requestFullscreen || video.webkitRequestFullscreen || video.msRequestFullscreen
        if (!entered && typeof request === 'function') {
          try {
            var result = request.call(video)
            if (result && typeof result.then === 'function') {
              result
                .then(function () {
                  markEntered()
                })
                .catch(function () {
                  if (!self.isNativeFullscreen() && !self.isExpanded()) self.expand()
                })
            }
          } catch (error) {
            if (!self.isNativeFullscreen() && !self.isExpanded()) this.expand()
          }
        }

        setTimeout(function () {
          if (!self.isNativeFullscreen() && !self.isExpanded() && !self.classList.contains('is-dismissed')) {
            self.expand()
          }
        }, 400)
      }

      exitNativeFullscreen() {
        if (this.video && typeof this.video.webkitExitFullscreen === 'function' && this.video.webkitDisplayingFullscreen) {
          try {
            this.video.webkitExitFullscreen()
            return
          } catch (error) {}
        }
        var exit = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen
        if (typeof exit === 'function') {
          var result = exit.call(document)
          if (result && typeof result.catch === 'function') {
            result.catch(function () {})
          }
        }
      }

      isNativeFullscreen() {
        var fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement
        return Boolean(
          fullscreenElement === this.video || (this.video && this.video.webkitDisplayingFullscreen)
        )
      }

      onFullscreenChange() {
        if (this.isNativeFullscreen()) {
          this.onNativeFullscreenEnter()
        } else if (!this.isExpanded()) {
          this.onNativeFullscreenExit()
        }
      }

      onNativeFullscreenEnter() {
        this.classList.add('is-native-fullscreen')
        this.enableSound()
      }

      onNativeFullscreenExit() {
        this.classList.remove('is-native-fullscreen')
        if (this.isExpanded()) return
        this.mutePreview()
        this.playPreview()
      }

      onKeyDown(event) {
        if (event.key === 'Escape' && this.isExpanded()) {
          event.preventDefault()
          this.collapse()
        }
      }
    }

    api.Player = WelcomeVideoPlayer

    if (typeof customElements !== 'undefined' && !customElements.get('welcome-video-player')) {
      customElements.define('welcome-video-player', WelcomeVideoPlayer)
    }
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = api
  }

  global.WelcomeVideo = api
})(typeof window !== 'undefined' ? window : globalThis)
